/**
 * Template editor modal — centered card with a TipTap rich-text body.
 *
 * Subject row → TipTap toolbar (Bold / Italic / Underline / Link / lists /
 * insert variable) → TipTap WYSIWYG editor → footer (/ Variable pill + Save).
 *
 * `body_html` is stored as HTML (what TipTap emits). The pipeline that sends
 * emails already consumes HTML body, so this is fully compatible.
 *
 * `{{VAR}}` tokens are plain text inside TipTap and survive round-trips.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Bold,
  Braces,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Loader2,
  Send,
  Underline as UnderlineIcon,
  Redo,
  Undo,
} from 'lucide-react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapUnderline from '@tiptap/extension-underline';
import TiptapLink from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { useAuth } from '@core/auth/useAuth';
import {
  emailTemplateService,
  type EmailTemplate,
} from '@core/supabase/services/emailTemplateService';
import { useToast } from '@shared/hooks/useToast';
import {
  getDefaultVariables,
  listEmailVariables,
} from '../lib/emailVariablesStore';

// ── Types ────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  /** When provided, opens in edit mode (preserves the key). */
  initial?: EmailTemplate | null;
  /** Pre-fill subject for a new template (starter template "Use"). */
  defaultSubject?: string;
  /** Pre-fill body for a new template. */
  defaultBody?: string;
  onClose: () => void;
  onSaved: (t: EmailTemplate) => void;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function slugifyKey(subject: string): string {
  const base = subject
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  const suffix = Math.random().toString(36).slice(2, 6);
  return base ? `${base}_${suffix}` : `tpl_${suffix}`;
}

// ── Variable picker popup ─────────────────────────────────────────────────────

function VariablePicker({
  orgId,
  onPick,
  onClose,
}: {
  orgId: string;
  onPick: (name: string) => void;
  onClose: () => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const orgVars = listEmailVariables(orgId);
  const defaults = getDefaultVariables();

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      ref={rootRef}
      className="absolute bottom-full left-0 z-[60] mb-1.5 w-[180px] overflow-hidden rounded-[8px] border shadow-lg"
      style={{ background: 'var(--vscode-bg-elevated)', borderColor: 'var(--vscode-border-primary)' }}
      role="menu"
    >
      {orgVars.length > 0 && (
        <div className="py-1">
          <div className="px-2.5 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--vscode-text-tertiary)' }}>
            Your variables
          </div>
          {orgVars.map((v) => (
            <button
              key={v.id}
              type="button"
              role="menuitem"
              onClick={() => { onPick(v.name); onClose(); }}
              className="block w-full truncate px-2.5 py-1.5 text-left text-[12px] hover:bg-[var(--vscode-bg-hover)]"
              style={{ color: 'var(--vscode-text-primary)' }}
            >
              {v.name.toLowerCase()}
            </button>
          ))}
        </div>
      )}
      <div className="py-1" style={{ borderTop: orgVars.length > 0 ? '1px solid var(--vscode-border-primary)' : 'none' }}>
        <div className="px-2.5 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--vscode-text-tertiary)' }}>
          Standard
        </div>
        {defaults.map((d) => (
          <button
            key={d.name}
            type="button"
            role="menuitem"
            onClick={() => { onPick(d.name); onClose(); }}
            className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[12px] hover:bg-[var(--vscode-bg-hover)]"
            title={d.hint}
            style={{ color: 'var(--vscode-text-primary)' }}
          >
            <span className="truncate">{d.name.toLowerCase().replace(/_/g, ' ')}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── TipTap Toolbar ────────────────────────────────────────────────────────────

function Toolbar({ editor, onOpenVariables }: { editor: Editor | null; onOpenVariables: () => void }) {
  if (!editor) return null;

  const Btn = ({ title, onClick, active, children }: { title: string; onClick: () => void; active?: boolean; children: React.ReactNode }) => (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`flex h-7 w-7 items-center justify-center rounded-[5px] transition-colors ${
        active
          ? 'bg-[color-mix(in_srgb,#10B981_20%,var(--vscode-bg-hover))] text-[#10B981]'
          : 'hover:bg-[var(--vscode-bg-hover)]'
      }`}
      style={{ color: active ? '#10B981' : 'var(--vscode-text-secondary)' }}
    >
      {children}
    </button>
  );

  const Sep = () => (
    <div className="mx-0.5 h-4 w-px" style={{ background: 'var(--vscode-border-primary)' }} />
  );

  return (
    <div
      className="flex flex-wrap items-center gap-0.5 border-b px-3 py-1.5"
      style={{ borderColor: 'var(--vscode-border-primary)' }}
    >
      <Btn title="Undo" onClick={() => editor.chain().focus().undo().run()}>
        <Undo className="h-3.5 w-3.5" />
      </Btn>
      <Btn title="Redo" onClick={() => editor.chain().focus().redo().run()}>
        <Redo className="h-3.5 w-3.5" />
      </Btn>
      <Sep />
      <Btn title="Bold" onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')}>
        <Bold className="h-3.5 w-3.5" />
      </Btn>
      <Btn title="Italic" onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')}>
        <Italic className="h-3.5 w-3.5" />
      </Btn>
      <Btn title="Underline" onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')}>
        <UnderlineIcon className="h-3.5 w-3.5" />
      </Btn>
      <Sep />
      <Btn
        title="Insert link"
        onClick={() => {
          const url = window.prompt('Enter URL', 'https://');
          if (!url) return;
          editor.chain().focus().setLink({ href: url }).run();
        }}
        active={editor.isActive('link')}
      >
        <LinkIcon className="h-3.5 w-3.5" />
      </Btn>
      <Sep />
      <Sep />
      <Btn title="Bullet list" onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')}>
        <List className="h-3.5 w-3.5" />
      </Btn>
      <Btn title="Ordered list" onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')}>
        <ListOrdered className="h-3.5 w-3.5" />
      </Btn>
      <Sep />
      <Btn title="Insert variable" onClick={onOpenVariables}>
        <Braces className="h-3.5 w-3.5" />
      </Btn>
    </div>
  );
}

// ── Main modal ───────────────────────────────────────────────────────────────

export function TemplateEditorModal({ open, initial, defaultSubject, defaultBody, onClose, onSaved }: Props) {
  const { user } = useAuth();
  const { toast, showToast } = useToast();
  const [subject, setSubject] = useState('');
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: false, blockquote: false, horizontalRule: false }),
      TiptapUnderline,
      TiptapLink.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: 'Start typing your email here…' }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: [
          'focus:outline-none min-h-[320px] px-4 py-3 text-[13px] leading-[1.6]',
          '[&_p]:mb-2 [&_p:last-child]:mb-0',
          '[&_strong]:font-semibold',
          '[&_em]:italic',
          '[&_u]:underline',
          '[&_a]:text-emerald-500 [&_a]:underline',
          '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1.5',
          '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1.5',
          '[&_li]:my-0.5',
        ].join(' '),
      },
    },
  });

  // Populate form when modal opens
  useEffect(() => {
    if (!open || !editor) return;
    const initSubject = initial?.subject ?? defaultSubject ?? '';
    const initBody = initial?.body_html ?? defaultBody ?? '';

    setSubject(initSubject);
    setPickerOpen(false);

    // Convert plain-text body (newlines) to paragraphs if no HTML detected
    const hasHtml = /<[a-z][\s\S]*>/i.test(initBody);
    if (hasHtml) {
      editor.commands.setContent(initBody, false);
    } else {
      // Wrap each line in a <p> so TipTap renders line breaks correctly
      const asHtml = initBody
        .split('\n')
        .map((line) => `<p>${line || '<br>'}</p>`)
        .join('');
      editor.commands.setContent(asHtml, false);
    }
  }, [open, initial, defaultSubject, defaultBody, editor]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const onOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === overlayRef.current) onClose();
    },
    [onClose],
  );

  const insertVariable = (name: string) => {
    if (!editor) return;
    editor.chain().focus().insertContent(`{{${name}}}`).run();
  };

  const canSave = subject.trim().length > 0 && !saving;

  const handleSave = async () => {
    if (!user?.orgId || !user.id) {
      showToast({ title: 'No active organisation.', kind: 'error' });
      return;
    }
    if (!subject.trim()) {
      showToast({ title: 'Subject is required.', kind: 'error' });
      return;
    }
    setSaving(true);
    try {
      const body_html = editor?.getHTML() ?? '';
      const payload = {
        key: initial?.key ?? slugifyKey(subject),
        name: subject.trim(),
        subject: subject.trim(),
        body_html,
        variables: initial?.variables ?? [],
      };
      const saved = await emailTemplateService.upsert(user.orgId, user.id, payload);
      showToast({ title: 'Template saved successfully.', kind: 'success' });
      onSaved(saved);
      onClose();
    } catch (err) {
      showToast({
        title: err instanceof Error ? err.message : 'Could not save template.',
        kind: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      onClick={onOverlayClick}
      className="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto px-4 py-10"
      style={{ background: 'rgba(17, 24, 39, 0.5)' }}
    >
      {toast}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={initial ? 'Edit template' : 'Create template'}
        className="relative flex w-full max-w-[720px] flex-col overflow-hidden rounded-[14px] shadow-2xl"
        style={{
          background: 'var(--vscode-bg-elevated)',
          border: '1px solid var(--vscode-border-primary)',
          minHeight: '540px',
          maxHeight: 'calc(100dvh - 80px)',
        }}
      >
        {/* Subject row */}
        <div
          className="flex items-center gap-3 border-b px-4 py-3"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          <label
            htmlFor="tpl-subject"
            className="shrink-0 text-[12.5px]"
            style={{ color: 'var(--vscode-text-tertiary)' }}
          >
            Subject:
          </label>
          <input
            id="tpl-subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Add a subject..."
            className="min-w-0 flex-1 bg-transparent text-[13px] font-medium outline-none"
            style={{ color: 'var(--vscode-text-primary)' }}
            autoFocus
          />
        </div>

        {/* TipTap Toolbar */}
        <Toolbar editor={editor} onOpenVariables={() => setPickerOpen((o) => !o)} />

        {/* TipTap editor area */}
        <div
          className="flex-1 overflow-y-auto"
          style={{ color: 'var(--vscode-text-primary)' }}
          onClick={() => editor?.chain().focus().run()}
        >
          <EditorContent
            editor={editor}
            className="h-full"
          />
        </div>

        {/* Footer */}
        <div
          className="relative flex items-center justify-between gap-3 border-t px-3 py-2.5"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          {/* Variable insert pill */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setPickerOpen((o) => !o)}
              className="inline-flex items-center gap-1.5 rounded-[6px] border px-2.5 py-1 text-[12px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
              style={{ borderColor: 'var(--vscode-border-primary)', color: 'var(--vscode-text-secondary)' }}
            >
              <span className="text-[12px]" style={{ color: 'var(--vscode-text-tertiary)' }}>/</span>
              Variable
            </button>
            {pickerOpen && user?.orgId && (
              <VariablePicker
                orgId={user.orgId}
                onPick={insertVariable}
                onClose={() => setPickerOpen(false)}
              />
            )}
          </div>

          {/* Save */}
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="inline-flex items-center gap-1.5 rounded-[8px] px-3.5 py-1.5 text-[12.5px] font-medium text-white transition-opacity disabled:opacity-50"
            style={{ background: '#10B981' }}
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Save Template
          </button>
        </div>
      </div>
    </div>
  );
}
